import type { ReactNode } from "react";

function H2({ children, id }: { children: ReactNode; id: string }) {
  return (
    <h2 id={id} className="mt-10 scroll-mt-20 text-lg font-semibold tracking-tight text-zinc-100">
      {children}
    </h2>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className="mt-3 leading-relaxed text-zinc-300">{children}</p>;
}

function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-zinc-900 px-1.5 py-0.5 font-mono text-[12px] text-zinc-200 ring-1 ring-zinc-800">
      {children}
    </code>
  );
}

function Pre({ children }: { children: ReactNode }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-950/80 p-4 font-mono text-[12px] leading-relaxed text-zinc-300">
      {children}
    </pre>
  );
}

export function Docs() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <header className="mb-2">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Documentation</h1>
        <p className="mt-1 text-sm text-zinc-500">
          How to use Kiosk Ops, what each panel means, and how the dashboard talks to
          Bajie / ChargeNow.
        </p>
      </header>

      <nav className="mt-6 rounded-lg border border-zinc-800 bg-zinc-950/40 p-4 text-sm">
        <div className="mb-2 text-[11px] uppercase tracking-wider text-zinc-500">On this page</div>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-zinc-300">
          <li><a className="hover:text-zinc-50" href="#what">What this is</a></li>
          <li><a className="hover:text-zinc-50" href="#fleet">Fleet view</a></li>
          <li><a className="hover:text-zinc-50" href="#cabinet">Cabinet detail</a></li>
          <li><a className="hover:text-zinc-50" href="#faulty">Faulty batteries</a></li>
          <li><a className="hover:text-zinc-50" href="#events">Live event stream</a></li>
          <li><a className="hover:text-zinc-50" href="#actions">Actions reference</a></li>
          <li><a className="hover:text-zinc-50" href="#api">Admin API</a></li>
          <li><a className="hover:text-zinc-50" href="#webhook">Webhook setup</a></li>
          <li><a className="hover:text-zinc-50" href="#data">How data gets in</a></li>
          <li><a className="hover:text-zinc-50" href="#troubleshooting">Troubleshooting</a></li>
        </ul>
      </nav>

      <H2 id="what">What this is</H2>
      <P>
        Kiosk Ops is the Boosted-side fleet console for the Bajie / ChargeNow power-bank
        rental kiosks. It consumes Bajie's operator API and webhook event push, normalizes
        everything into Cloudflare D1, and exposes a dashboard plus a small admin API
        for fleet-management actions.
      </P>
      <P>
        The dashboard is read-mostly and operations-light: every action button (restart,
        pop, lock, eject for repair) ultimately calls Bajie's API on your behalf — the
        kiosks themselves stay unmodified.
      </P>

      <H2 id="fleet">Fleet view</H2>
      <P>The home page shows four numbers up top:</P>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-zinc-300">
        <li><strong>Cabinets online</strong> — how many cabinets reported themselves online vs total registered.</li>
        <li><strong>Batteries OK</strong> — how many batteries are reporting <Code>faultType=0</Code> vs total inventory.</li>
        <li>
          <strong>Events</strong> — breakdown of webhook activity, scoped by the
          {" "}<Code>24h</Code> / <Code>all-time</Code> toggle.
          <Code>Rentals</Code> counts <Code>BATTERY_BORROW_OUT</Code>,
          {" "}<Code>Returns</Code> counts <Code>BATTERY_IN</Code>,
          {" "}<Code>Problems</Code> counts <Code>BATTERY_ABNORMAL_WARNING</Code>.
          The footer shows the all-time event count across every event type
          (status blips and admin events included) as a pipeline smoke check.
        </li>
        <li><strong>Refresh</strong> — auto-poll cadence (the dashboard refetches every 5 s).</li>
      </ul>
      <P>
        Below that, every cabinet is shown as a card with online state, signal strength,
        slot utilization, and quick <Code>Restart</Code> / <Code>Heartbeat</Code> actions.
        Click a cabinet to see its detail.
      </P>

      <H2 id="cabinet">Cabinet detail</H2>
      <P>
        The cabinet detail page (<Code>/cabinets/:id</Code>) is where every action on
        a kiosk lives. Three panels stacked top to bottom:
      </P>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-zinc-300">
        <li><strong>Cabinet actions</strong> — kiosk-level operations (no slot needed): Restart, Heartbeat, Report, Pop all, Pop all (auth), Pop all (no auth). Destructive ones require a second click to confirm. "Refresh from Bajie" pulls fresh state.</li>
        <li><strong>Slot grid</strong> — laid out like the physical kiosk. Click any slot to inspect or act on the battery in it: Pop, Lock, Unlock, Eject for repair.</li>
        <li><strong>Signal chart</strong> — 24h trend, see <a href="#chart" className="text-zinc-100 hover:underline">Signal chart</a>.</li>
      </ul>
      <P>Slot colors:</P>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-zinc-300">
        <li><span className="text-zinc-600">dashed</span> — empty slot</li>
        <li><span className="text-emerald-300">green</span> — occupied, healthy battery</li>
        <li><span className="text-amber-300">amber</span> — occupied, minor fault (codes 1–6)</li>
        <li><span className="text-rose-300">rose</span> — occupied, hard fault (codes 7+)</li>
      </ul>
      <P>
        <Code>Refresh from Bajie</Code> bypasses our D1 cache and pulls the cabinet's
        live state directly via <Code>GET /rent/cabinet/query</Code>. Useful when you want
        ground-truth right now.
      </P>

      <H2 id="faulty">Faulty batteries</H2>
      <P>
        The faulty batteries view lists every battery currently reporting a non-zero
        fault. The "Eject for repair" button calls Bajie's dedicated{" "}
        <Code>/cabinet/repairEject</Code> endpoint, which marks the battery as
        repair-mode in Bajie's system and pops it from the slot for service.
      </P>
      <P>Battery fault enum (from Bajie's webhook payload spec):</P>
      <Pre>{`0  ok / normal
1  virtual power
2  Apple connector fault
3  USB connector fault
4  USB-C fault
5  swelling                ⚠ remove from service immediately
6  authorization failed
7  cannot charge           ⚠ hard fault
8  duplicate battery id
9  battery recall
10 health substandard
11 overuse limit`}</Pre>

      <H2 id="uptime">Uptime SLA</H2>
      <P>
        <Code>/uptime</Code> ranks every cabinet by % of snapshots reporting online
        over a chosen window (24h / 7d / 30d / 90d). Coverage starts at zero and fills
        in as the cron worker captures more samples (every 5 min). Color tiers: ≥99%
        green, ≥95% amber, &lt;95% rose.
      </P>

      <H2 id="lifecycle">Battery lifecycle</H2>
      <P>
        Click any battery ID (e.g. on the faulty-batteries table) to see its full
        timeline: every <Code>BATTERY_BORROW_OUT</Code>, <Code>BATTERY_IN</Code>, and
        fault event. Stats panel shows total rentals, returns, average rental duration
        (computed from BORROW_OUT → BATTERY_IN pairs), and whether the battery is
        currently out.
      </P>

      <H2 id="rentals">Active rentals</H2>
      <P>
        <Code>/rentals/active</Code> lists every battery currently borrowed out — derived
        from <Code>BATTERY_BORROW_OUT</Code> events with no subsequent <Code>BATTERY_IN</Code>.
        The duration pill turns amber after 4 hours and rose after 24 hours, surfacing
        stuck rentals.
      </P>

      <H2 id="chart">Signal chart</H2>
      <P>
        Each cabinet detail page includes a 24-hour signal-strength trend rendered from
        the <Code>cabinet_snapshots</Code> table. A Cron-Triggered Worker snapshots every
        cabinet's cached state every 5 minutes — so even when a cabinet is quiet, you get
        a heartbeat-style time series. The amber dashed line at 19 is Bajie's
        connectivity threshold; below that, kiosks may drop offline.
      </P>

      <H2 id="discord">Discord alerts</H2>
      <P>
        If <Code>DISCORD_WEBHOOK_URL</Code> is set as a Worker secret, the receiver
        fans out severity events to that webhook in real time:
        <Code>CABINET_OFFLINE</Code>, <Code>CABINET_ONLINE</Code>,{" "}
        <Code>BATTERY_ABNORMAL_WARNING</Code>, <Code>BATTERY_POPUP</Code>. Embeds
        include cabinet ID, battery ID, slot, fault label, and timestamp.
      </P>
      <P>To enable:</P>
      <Pre>{`bun --cwd api wrangler secret put DISCORD_WEBHOOK_URL
# paste a Discord channel webhook URL when prompted`}</Pre>

      <H2 id="events">Live event stream</H2>
      <P>
        The right-hand panel on the home page shows the most recent 20 webhook events as
        they arrive. The two-line layout is intentional — the top line gives you the event
        type and timestamp; the bottom line shows the cabinet ID and the most relevant
        field from the payload (which battery, which fault, signal strength, etc.).
      </P>
      <P>Subscribable event types:</P>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-zinc-300">
        <li><Code>CABINET_ONLINE</Code> / <Code>CABINET_OFFLINE</Code> — connectivity transitions</li>
        <li><Code>CABINET_STATUS</Code> — full snapshot, fired on heartbeat</li>
        <li><Code>BATTERY_IN</Code> — a battery was returned to a slot</li>
        <li><Code>BATTERY_BORROW_OUT</Code> — a battery was rented out</li>
        <li><Code>BATTERY_ABNORMAL_WARNING</Code> — fault detected on a battery</li>
        <li><Code>BATTERY_POPUP</Code> — a slot tried to pop and failed</li>
        <li><Code>POS_INFO_STATUS</Code> — POS subsystem state change</li>
        <li><Code>ADMIN_RENTAL_ORDER</Code> — backstage rental order</li>
      </ul>

      <H2 id="actions">Actions reference</H2>
      <P>Every action button proxies through to Bajie's <Code>/cabinet/operation</Code> with a specific <Code>operationType</Code>:</P>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-zinc-300">
        <li><Code>restart</Code> — soft reboot the kiosk's controller. First thing to try when a cabinet is misbehaving.</li>
        <li><Code>heartbeat</Code> — request a fresh <Code>CABINET_STATUS</Code> push. Lightweight ping.</li>
        <li><Code>pop</Code> — eject the battery from one slot. Useful for manual returns or stuck batteries.</li>
        <li><Code>lock</Code> — keep the battery in place; user can't borrow until unlocked. Use to isolate a suspect battery.</li>
        <li><Code>unlock</Code> — undo a lock.</li>
        <li><Code>lockStopCharge</Code> — lock <em>and</em> stop charging. Use for swelling or overheating cells.</li>
        <li><Code>popall</Code> / <Code>popallForAuth</Code> / <Code>popallForNoAuth</Code> — eject everything (service mode).</li>
        <li><Code>report</Code> — pull a fresh status report.</li>
      </ul>
      <P>
        "Eject for repair" is separate — it hits Bajie's dedicated repair endpoint so
        the battery is flagged in their system, not just popped. Prefer this over plain{" "}
        <Code>pop</Code> when you've actually identified the battery as faulty.
      </P>

      <H2 id="api">Admin API</H2>
      <P>All routes under <Code>/api</Code>, JSON in/out. Same-origin from the dashboard.</P>
      <Pre>{`GET    /api/stats                          fleet headline numbers
GET    /api/cabinets                       list all cabinets
GET    /api/cabinets/:id                   cabinet + batteries
GET    /api/cabinets/:id/events            recent events
GET    /api/cabinets/:id/refresh           live pull from Bajie
POST   /api/cabinets/:id/operation         { operationType, slotNum, reason }
GET    /api/batteries                      list all batteries
GET    /api/batteries?fault=true           only faulty batteries
GET    /api/batteries?fault=N              only fault code = N
POST   /api/batteries/:id/eject-repair     { reason? }
GET    /api/batteries/rotations            top-N batteries by BORROW_OUT count
GET    /api/rentals/active                 currently-borrowed batteries
GET    /api/cabinets/:id/snapshots?hours=N signal/uptime time series
POST   /api/cron/snapshot                  manually trigger one snapshot
POST   /api/sync                           bulk sync from Bajie's Get Device
                                           List (operator API)
POST   /api/webhook/sync                   { pushUrl, events? } — calls Bajie
                                           Set Cabinet Event Push Config
GET    /api/uptime/fleet?days=N            % online over window per cabinet
GET    /api/batteries/:id/lifecycle        full battery timeline + stats
GET    /api/events                         paginated event log
                                           ?event=...&cabinetId=...&limit=...`}</Pre>

      <H2 id="webhook">Webhook setup</H2>
      <P>
        The webhook receiver lives at <Code>/webhook/bajie</Code>. It's gated by HTTP
        Basic auth (separate from the admin API). To start receiving events, call Bajie's
        <Code>POST /cabinet/eventPush/config</Code> with our URL and the events you want:
      </P>
      <Pre>{`curl -u "$BAJIE_USERNAME:$BAJIE_PASSWORD" \\
  -H "Content-Type: application/json" \\
  -X POST https://developer.chargenow.top/cdb-open-api/v1/cabinet/eventPush/config \\
  -d '{
    "pushUrl": "https://WEBHOOK_USER:WEBHOOK_PASS@kiosk.challon.io/webhook/bajie",
    "eventSubscriptions": [
      { "event": "CABINET_ONLINE",            "enable": true },
      { "event": "CABINET_OFFLINE",           "enable": true },
      { "event": "CABINET_STATUS",            "enable": true },
      { "event": "BATTERY_IN",                "enable": true },
      { "event": "BATTERY_BORROW_OUT",        "enable": true },
      { "event": "BATTERY_ABNORMAL_WARNING",  "enable": true },
      { "event": "BATTERY_POPUP",             "enable": true },
      { "event": "POS_INFO_STATUS",           "enable": true },
      { "event": "ADMIN_RENTAL_ORDER",        "enable": true }
    ]
  }'`}</Pre>

      <H2 id="data">How data gets in</H2>
      <P>Two paths populate the dashboard:</P>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-zinc-300">
        <li><strong>Webhook push</strong> — Bajie POSTs every event in real time to our receiver. Each event is validated, deduped on{" "}
          <Code>cabinetId | event | timestamp | firstBatteryId</Code>, and stored.
          Cabinet and battery state tables are upserted so the dashboard always shows the latest.
        </li>
        <li><strong>Refresh from Bajie</strong> — explicit pull via <Code>GET /rent/cabinet/query</Code>.
          Used by the Refresh button on cabinet detail.
        </li>
      </ul>
      <P>
        Bajie's webhook spec doesn't document HMAC signing, retries, or an idempotency
        key, so the receiver is built defensively: HTTP Basic auth + Zod schema validation
        on every payload + a synthetic dedup key so duplicate deliveries are no-ops.
      </P>

      <H2 id="troubleshooting">Troubleshooting</H2>
      <ul className="mt-3 list-disc space-y-1 pl-6 text-zinc-300">
        <li><strong>Dashboard shows "no events"</strong> — webhook push hasn't been configured at Bajie yet, or the auth credentials in the pushUrl are wrong. Re-run the curl above with current creds.</li>
        <li><strong>Action button returns 502</strong> — Bajie API call failed. Check the cabinet is online; check Worker logs in Cloudflare dashboard. The error message in the response is verbatim from Bajie.</li>
        <li><strong>"Just a moment…" page in the browser</strong> — Cloudflare WAF challenge. The challon.io zone has a US-only rule; non-US clients hit it.</li>
        <li><strong>Cabinet shown offline but you can ping it</strong> — could be a stale state. Hit "Refresh from Bajie" on the cabinet detail page; that bypasses our cache.</li>
      </ul>
    </div>
  );
}
